// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';
import { identifyUser, resetAnalytics } from '../lib/analytics';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userOverride, setUserOverride] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const checkSession = async () => {
      const currentUser = authService.getCurrentUser();
      setUser(currentUser);
      // Restaurar modo técnico si el admin lo eligió en esta sesión
      if (currentUser?.role === 'admin') {
        const mode = sessionStorage.getItem('zapata_admin_mode');
        if (mode === 'tecnico') {
          setUserOverride({ ...currentUser, role: 'tecnico', employeeId: 1 });
        }
      }
      // Reestablece la sesión de Firebase Auth (para que request.auth != null en
      // las reglas de Firestore). Con timeout para no bloquear el arranque si la
      // red va mal — el SDK reintentará por su cuenta.
      if (currentUser) {
        try {
          await Promise.race([
            authService.ensureFirebaseAuth(),
            new Promise(res => setTimeout(res, 4000)),
          ]);
        } catch { /* no fatal */ }
      }
      if (!cancelled) setIsLoading(false);
    };
    checkSession();
    return () => { cancelled = true; };
  }, []);

  const login = async (email, password) => {
    const result = await authService.login(email, password);
    if (result.success) setUser(result.user);
    return result;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setUserOverride(null);
    sessionStorage.removeItem('zapata_admin_mode');
    sessionStorage.removeItem('zapata_current_view');
    resetAnalytics();
  };

  const updateUser = (userData) => {
    authService.updateUser(userData);
    setUser(prev => ({ ...prev, ...userData }));
  };

  const enterTechnicianMode = () => {
    setUserOverride(prev => ({ ...user, role: 'tecnico', employeeId: 1 }));
    sessionStorage.setItem('zapata_admin_mode', 'tecnico');
  };

  const exitTechnicianMode = () => {
    setUserOverride(null);
    sessionStorage.setItem('zapata_admin_mode', 'director');
  };

  const effectiveUser = userOverride || user;

  // Identifica en PostHog con el rol real (técnico/oficina/dirección) cada
  // vez que cambia el usuario efectivo o entra/sale del modo "Cambiar a
  // Técnico" — así la simulación de un admin no se mezcla con datos reales
  // de Pablo (ver isAdminImpersonating).
  useEffect(() => {
    identifyUser(effectiveUser, { isAdminImpersonating: !!userOverride });
  }, [effectiveUser, userOverride]);

  // Aplica la clase `dark` en <html> según la preferencia del usuario —
  // único sitio de aplicación, válido tanto para la app de oficina como
  // para la app de técnicos (ambas consumen este mismo contexto).
  useEffect(() => {
    document.documentElement.classList.toggle('dark', effectiveUser?.theme === 'dark');
  }, [effectiveUser?.theme]);

  const value = {
    user: effectiveUser,
    isAdminImpersonating: !!userOverride,
    login,
    logout,
    updateUser,
    enterTechnicianMode,
    exitTechnicianMode,
    isAuthenticated: !!effectiveUser,
    isLoading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};